import React, { useState, useEffect, useCallback } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { Action } from '../lib/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Alert,
  AlertDescription,
} from '../components/ui/alert';
import {
  Mail,
  Bell,
  Settings,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  AlertCircle,
  Loader2,
  Plus,
  X,
  Building2,
  RefreshCw,
  Key,
  ExternalLink,
  FileText,
  Eye,
  Save,
  Edit3
} from 'lucide-react';
import { apiErrorMessage } from '../lib/apiError';
import { toast } from 'sonner';
import api from '../lib/api';

const EmailNotificationsPage = () => {
  const { activeProject, can } = useProject();
  const { isAdmin } = useAuth();
  // The Resend API key, send-test, and template-edit endpoints are ADMIN-only
  // (org axis) — NOT project-axis MANAGE_SCHEDULES. Gate those controls on the
  // org role so a project planner isn't shown an action the backend 403s.
  const canManageEmailConfig = isAdmin();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  
  // API Key state
  const [apiKeyStatus, setApiKeyStatus] = useState({ configured: false, masked_key: null });
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [savingApiKey, setSavingApiKey] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    recipients: [],
    notify_activity_start: true,
    notify_activity_end: true,
    notify_in_progress: true,
    notify_weekly_summary: true,
    is_enabled: true
  });
  const [newRecipient, setNewRecipient] = useState('');
  
  // Test email dialog
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  
  // Template editing state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateEditData, setTemplateEditData] = useState({ subject: '', body: '' });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [templatePreviewHtml, setTemplatePreviewHtml] = useState('');
  const [activeTab, setActiveTab] = useState('settings');
  
  const canEdit = can(Action.MANAGE_SCHEDULES);
  
  // Fetch API key status
  const fetchApiKeyStatus = useCallback(async () => {
    try {
      const response = await api.get('/email-notifications/api-key/status');
      setApiKeyStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch API key status:', error);
    }
  }, []);
  
  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const response = await api.get('/email-notifications/templates/all');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);
  
  // Fetch settings
  const fetchSettings = useCallback(async () => {
    if (!activeProject?.id) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/email-notifications/settings/${activeProject.id}`);
      if (response.data) {
        setSettings(response.data);
        setFormData({
          recipients: response.data.recipients || [],
          notify_activity_start: response.data.notify_activity_start ?? true,
          notify_activity_end: response.data.notify_activity_end ?? true,
          notify_in_progress: response.data.notify_in_progress ?? true,
          notify_weekly_summary: response.data.notify_weekly_summary ?? true,
          is_enabled: response.data.is_enabled ?? true
        });
      } else {
        setSettings(null);
        setFormData({
          recipients: [],
          notify_activity_start: true,
          notify_activity_end: true,
          notify_in_progress: true,
          notify_weekly_summary: true,
          is_enabled: true
        });
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch email settings:', error);
      }
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, [activeProject?.id]);
  
  // Fetch email logs
  const fetchLogs = useCallback(async () => {
    if (!activeProject?.id) return;
    
    setLogsLoading(true);
    try {
      const response = await api.get(`/email-notifications/logs/${activeProject.id}?limit=20`);
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch email logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, [activeProject?.id]);
  
  useEffect(() => {
    fetchApiKeyStatus();
    fetchTemplates();
  }, [fetchApiKeyStatus, fetchTemplates]);
  
  useEffect(() => {
    fetchSettings();
    fetchLogs();
  }, [fetchSettings, fetchLogs]);
  
  // Save API key
  const handleSaveApiKey = async () => {
    if (!newApiKey.trim()) return;
    
    setSavingApiKey(true);
    try {
      const response = await api.post('/email-notifications/api-key', { api_key: newApiKey.trim() });
      setApiKeyStatus(response.data);
      setShowApiKeyDialog(false);
      setNewApiKey('');
      toast.success('API key saved successfully');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save API key'));
    } finally {
      setSavingApiKey(false);
    }
  };
  
  // Save settings
  const handleSave = async () => {
    if (!activeProject?.id) return;
    
    setSaving(true);
    try {
      if (settings) {
        await api.put(`/email-notifications/settings/${activeProject.id}`, formData);
        toast.success('Settings updated');
      } else {
        await api.post('/email-notifications/settings', {
          project_id: activeProject.id,
          ...formData
        });
        toast.success('Settings saved');
      }
      await fetchSettings();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };
  
  // Add recipient
  const handleAddRecipient = () => {
    const email = newRecipient.trim().toLowerCase();
    if (!email) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (formData.recipients.includes(email)) {
      toast.error('This email is already added');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      recipients: [...prev.recipients, email]
    }));
    setNewRecipient('');
  };
  
  // Remove recipient
  const handleRemoveRecipient = (email) => {
    setFormData(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r !== email)
    }));
  };
  
  // Send test email
  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) return;
    
    setSendingTest(true);
    try {
      const response = await api.post('/email-notifications/test', {
        recipient_email: testEmail.trim()
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowTestDialog(false);
        setTestEmail('');
        fetchLogs();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to send test email'));
    } finally {
      setSendingTest(false);
    }
  };
  
  // Template editing functions
  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateEditData({
      subject: template.default_subject || '',
      body: template.default_body || ''
    });
  };
  
  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    setSavingTemplate(true);
    try {
      await api.put(`/email-notifications/templates/${editingTemplate.id}`, {
        default_subject: templateEditData.subject,
        default_body: templateEditData.body
      });
      toast.success('Template saved successfully');
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save template'));
    } finally {
      setSavingTemplate(false);
    }
  };
  
  const handlePreviewTemplate = async (template) => {
    try {
      const response = await api.post('/email-notifications/preview', {
        recipients: ['preview@example.com'],
        subject: template.default_subject || 'Preview Subject',
        message_body: template.default_body || 'This is a preview of the email template.',
        template_type: template.id
      });
      setTemplatePreviewHtml(response.data.html);
      setShowTemplatePreview(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const getNotificationTypeBadge = (type) => {
    const config = {
      activity_start: { label: 'Start', className: 'bg-blue-100 text-blue-700' },
      activity_end: { label: 'End', className: 'bg-green-100 text-green-700' },
      activity_in_progress: { label: 'In Progress', className: 'bg-amber-100 text-amber-700' },
      weekly_summary: { label: 'Weekly', className: 'bg-purple-100 text-purple-700' }
    };
    const c = config[type] || { label: type, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={`text-[10px] ${c.className}`}>{c.label}</Badge>;
  };
  
  // No project selected
  if (!activeProject) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-12rem)]">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
          <Building2 className="h-8 w-8 text-slate-300" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900 mb-2">No Project Selected</h2>
        <p className="text-sm text-slate-500 text-center max-w-sm">
          Select a project from the header to configure email notifications.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="email-notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="h-6 w-6 text-blue-600" />
            Email Notifications
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {activeProject.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {apiKeyStatus.configured && canManageEmailConfig && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTestDialog(true)}
              data-testid="send-test-email-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Send Test
            </Button>
          )}
        </div>
      </div>
      
      {/* API Key Setup Alert */}
      {!apiKeyStatus.configured && (
        <Alert className="border-amber-200 bg-amber-50">
          <Key className="h-4 w-4 text-amber-600" />
          <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="font-medium text-amber-800">Setup Required: </span>
              <span className="text-amber-700">Add your Resend API key to enable email notifications.</span>
            </div>
            {canManageEmailConfig && (
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 sm:ml-4 border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => setShowApiKeyDialog(true)}
              >
                <Key className="h-4 w-4 mr-2" />
                Add API Key
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}
      
      {/* API Key Configured */}
      {apiKeyStatus.configured && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-green-700">
              <span className="font-medium">Connected: </span>
              Resend API Key {apiKeyStatus.masked_key}
            </span>
            {canManageEmailConfig && (
              <Button
                size="sm"
                variant="ghost"
                className="text-green-700 hover:bg-green-100"
                onClick={() => setShowApiKeyDialog(true)}
              >
                Change Key
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <FileText className="h-4 w-4" />
            Templates
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Settings Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Settings className="h-5 w-5" />
                Notification Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <>
                  {/* Master Toggle */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-base font-medium">Enable Notifications</Label>
                      <p className="text-sm text-slate-500">Master switch for all email notifications</p>
                    </div>
                    <Switch
                      checked={formData.is_enabled}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_enabled: checked }))}
                      disabled={!canEdit}
                      data-testid="enable-notifications-switch"
                    />
                  </div>
                  
                  <Separator />
                  
                  {/* Recipients */}
                  <div className="space-y-3">
                    <Label>Email Recipients</Label>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="Enter email address"
                        value={newRecipient}
                        onChange={(e) => setNewRecipient(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAddRecipient()}
                        disabled={!canEdit}
                        className="flex-1"
                        data-testid="recipient-input"
                      />
                      <Button
                        variant="outline"
                        onClick={handleAddRecipient}
                        disabled={!canEdit || !newRecipient.trim()}
                        data-testid="add-recipient-btn"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    {formData.recipients.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {formData.recipients.map((email) => (
                          <Badge key={email} variant="secondary" className="pl-2 pr-1 py-1">
                            <Mail className="h-3 w-3 mr-1" />
                            {email}
                            {canEdit && (
                              <button
                                onClick={() => handleRemoveRecipient(email)}
                                className="ml-1 hover:bg-slate-300 rounded p-0.5"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 italic">No recipients added</p>
                    )}
                  </div>
                  
                  <Separator />
                  
                  {/* Notification Types */}
                  <div className="space-y-4">
                    <Label>Notification Types</Label>
                    
                    <div className="grid gap-3">
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <Clock className="h-4 w-4 text-blue-500" />
                          <div>
                            <p className="text-sm font-medium">Activity Start</p>
                            <p className="text-xs text-slate-500">When an activity starts</p>
                          </div>
                        </div>
                        <Switch
                          checked={formData.notify_activity_start}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notify_activity_start: checked }))}
                          disabled={!canEdit || !formData.is_enabled}
                          data-testid="notify-start-switch"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <div>
                            <p className="text-sm font-medium">Activity End</p>
                            <p className="text-xs text-slate-500">When an activity is completed</p>
                          </div>
                        </div>
                        <Switch
                          checked={formData.notify_activity_end}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notify_activity_end: checked }))}
                          disabled={!canEdit || !formData.is_enabled}
                          data-testid="notify-end-switch"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <RefreshCw className="h-4 w-4 text-amber-500" />
                          <div>
                            <p className="text-sm font-medium">In Progress</p>
                            <p className="text-xs text-slate-500">When activity moves to In Progress</p>
                          </div>
                        </div>
                        <Switch
                          checked={formData.notify_in_progress}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notify_in_progress: checked }))}
                          disabled={!canEdit || !formData.is_enabled}
                          data-testid="notify-progress-switch"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <Calendar className="h-4 w-4 text-purple-500" />
                          <div>
                            <p className="text-sm font-medium">Weekly Summary</p>
                            <p className="text-xs text-slate-500">Every Friday with upcoming activities</p>
                          </div>
                        </div>
                        <Switch
                          checked={formData.notify_weekly_summary}
                          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, notify_weekly_summary: checked }))}
                          disabled={!canEdit || !formData.is_enabled}
                          data-testid="notify-weekly-switch"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Save Button */}
                  {canEdit && (
                    <div className="flex justify-end pt-4">
                      <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
                        {saving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Save Settings
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Email Logs */}
        <div>
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Recent Emails
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={fetchLogs} disabled={logsLoading}>
                  <RefreshCw className={`h-4 w-4 ${logsLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <ScrollArea className="h-[350px]">
                {logsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                  </div>
                ) : logs.length === 0 ? (
                  <div className="text-center py-8">
                    <Mail className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No emails sent yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {logs.map((log) => (
                      <div key={log.id} className="p-2 bg-slate-50 rounded-lg border text-xs">
                        <div className="flex items-center justify-between mb-1">
                          {getNotificationTypeBadge(log.notification_type)}
                          {log.status === 'sent' ? (
                            <CheckCircle className="h-3 w-3 text-green-500" />
                          ) : (
                            <XCircle className="h-3 w-3 text-red-500" />
                          )}
                        </div>
                        <p className="font-medium text-slate-700 line-clamp-1">{log.subject}</p>
                        <p className="text-slate-400 text-[10px]">{formatDate(log.sent_at)}</p>
                        {log.error_message && (
                          <p className="text-red-500 text-[10px] mt-1">{log.error_message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-5 w-5" />
                Email Templates
              </CardTitle>
              <CardDescription>
                View and customize email templates used for system notifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              {templatesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : templates.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-500">No templates available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {templates.map((template) => (
                    <div 
                      key={template.id}
                      className="p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`template-${template.id}`}
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-slate-900">{template.name}</h3>
                            <Badge variant="outline" className="text-xs">
                              {template.id}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-500 mb-3">{template.description}</p>
                          
                          {/* Template content preview */}
                          <div className="space-y-2">
                            <div className="text-xs">
                              <span className="text-slate-400">Default Subject:</span>
                              <p className="text-slate-600 font-medium mt-0.5">
                                {template.default_subject || 'Not set'}
                              </p>
                            </div>
                            <div className="text-xs">
                              <span className="text-slate-400">Default Body Preview:</span>
                              <p className="text-slate-600 mt-0.5 line-clamp-2">
                                {template.default_body || 'Not set'}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 sm:ml-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePreviewTemplate(template)}
                            data-testid={`preview-template-${template.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {/* Template edit is ADMIN-only (PUT /templates/{id}),
                              not project-axis canEdit. */}
                          {canManageEmailConfig && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditTemplate(template)}
                              data-testid={`edit-template-${template.id}`}
                            >
                              <Edit3 className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* API Key Dialog */}
      <Dialog open={showApiKeyDialog} onOpenChange={setShowApiKeyDialog}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-blue-500" />
              Resend API Key
            </DialogTitle>
            <DialogDescription>
              Enter your Resend API key to enable email notifications.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="api-key">API Key</Label>
              <Input
                id="api-key"
                type="password"
                placeholder="re_xxxxxxxxx"
                value={newApiKey}
                onChange={(e) => setNewApiKey(e.target.value)}
                data-testid="api-key-input"
              />
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 text-sm">
              <p className="font-medium text-blue-800 mb-2">Get your free API key:</p>
              <ol className="list-decimal list-inside text-blue-700 space-y-1 text-xs">
                <li>Go to <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">resend.com <ExternalLink className="h-3 w-3" /></a></li>
                <li>Sign up (free, no credit card)</li>
                <li>Go to API Keys → Create API Key</li>
                <li>Copy and paste the key above</li>
              </ol>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowApiKeyDialog(false)} disabled={savingApiKey}>
              Cancel
            </Button>
            <Button onClick={handleSaveApiKey} disabled={savingApiKey || !newApiKey.trim()} data-testid="save-api-key-btn">
              {savingApiKey ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Key'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Test Email Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-500" />
              Send Test Email
            </DialogTitle>
            <DialogDescription>
              Verify your email configuration is working.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="test-email">Recipient Email</Label>
              <Input
                id="test-email"
                type="email"
                placeholder="your@email.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                data-testid="test-email-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTestDialog(false)} disabled={sendingTest}>
              Cancel
            </Button>
            <Button onClick={handleSendTestEmail} disabled={sendingTest || !testEmail.trim()} data-testid="confirm-test-email-btn">
              {sendingTest ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Test
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Edit Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={(open) => !open && setEditingTemplate(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-blue-500" />
              Edit Template: {editingTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              Customize the default subject and body for this email template
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template-subject">Default Subject</Label>
              <Input
                id="template-subject"
                placeholder="Enter default subject line"
                value={templateEditData.subject}
                onChange={(e) => setTemplateEditData(prev => ({ ...prev, subject: e.target.value }))}
                data-testid="template-subject-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="template-body">Default Body</Label>
              <Textarea
                id="template-body"
                placeholder="Enter default email body content..."
                value={templateEditData.body}
                onChange={(e) => setTemplateEditData(prev => ({ ...prev, body: e.target.value }))}
                rows={8}
                className="resize-none"
                data-testid="template-body-input"
              />
              <p className="text-xs text-slate-400">
                Plain text content. HTML formatting will be applied by the template.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingTemplate(null)} disabled={savingTemplate}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate} disabled={savingTemplate} data-testid="save-template-btn">
              {savingTemplate ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={showTemplatePreview} onOpenChange={setShowTemplatePreview}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
            <DialogDescription>
              Preview how emails using this template will look
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg overflow-hidden bg-slate-100 p-4">
            <div 
              className="bg-white rounded shadow-sm"
              dangerouslySetInnerHTML={{ __html: templatePreviewHtml }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplatePreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailNotificationsPage;
